import React, { useEffect, useRef } from "react";

import { useTheme } from "@mui/material/styles";

import * as echarts from "echarts";

import square_green_icon from 'assets/images/icons/icn_chart_square_green.svg';
import square_blue_icon from 'assets/images/icons/icn_chart_square_blue.svg';
import { formatTimeRange } from "./timeRange";

// 樣式表
const iconStyleSheet = {
    green: square_green_icon,
    blue: square_blue_icon
}

const lineStyleSheet = (theme) => ({
    green: {
        lineStyle: { color: theme.palette.primary.greenPrimary, width: 2.2 },
        itemStyle: { color: theme.palette.primary.greenPrimary },
        areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: theme.palette.primary.greenGradientPrimary },
                { offset: 0.5, color: theme.palette.primary.greenGradientSecound },
                { offset: 0.8, color: theme.palette.primary.greenGradientThird },
                { offset: 1, color: theme.palette.primary.rgbTransparent },
            ])
        }
    },
    blue: {
        lineStyle: { color: theme.palette.primary.main, width: 2.2 },
        itemStyle: { color: theme.palette.primary.main },
        areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: theme.palette.primary.mainGradientPrimary },
                { offset: 0.5, color: theme.palette.primary.mainGradientSecound },
                { offset: 0.8, color: theme.palette.primary.mainGradientThird },
                { offset: 1, color: theme.palette.primary.rgbTransparent },
            ])
        }
    },
});


const LineChart = ({ legendStyle, formattedLegendName, lineChartData, unitData, height }) => {
    const theme = useTheme();

    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    const styleSheet = lineStyleSheet(theme);
    const { top, right } = legendStyle;
    const { yAxisName, tooltipUnit, timeRangeUnit, timeRangePosition = "end" } = unitData;

    useEffect(() => {
        const chartDom = chartRef.current;
        if (!chartDom) return;

        // 初始化 ECharts
        const chart = echarts.init(chartDom, null, {
            renderer: 'svg',
            useDirtyRect: true,
        });
        chartInstance.current = chart;

        // setting
        const option = {
            // 顯示資訊
            legend: {
                top,
                right,
                textStyle: {
                    fontFamily: theme.typography.fontFamily,
                    color: theme.palette.primary.greyPrimary,
                    fontSize: theme.typography.customFontSize[2],
                },
                data: lineChartData.map((item) => ({
                    name: item.name,
                    icon: `image://${iconStyleSheet[item.type]}`
                })),

                formatter: (name) => formattedLegendName ? formattedLegendName[name] : name,
            },
            // hover 效果
            tooltip: {
                trigger: "axis",
                backgroundColor: theme.palette.primary.greyPrimaryTransparency,
                textStyle: { color: theme.palette.primary.white },
                extraCssText: `border-radius: 5px; max-width: 300px;`,
                formatter: (params) => {
                    if (!params || params.length === 0) return "";

                    const timeRange = formatTimeRange(
                        params[0].value[0],
                        timeRangeUnit,
                        timeRangePosition
                    );

                    let tooltipText = `<div style="font-size: 12px;">
                                            ${timeRange}
                                        </div>`;

                    params.forEach(param => {
                        const findIcon = lineChartData.find(item => item.name === param.seriesName);
                        const iconSrc = iconStyleSheet[findIcon.type];
                        tooltipText += `
                            <div style="display:flex; justify-content: space-between; align-items:center; font-size:12px; color:#FFFFFF;">
                                <span><img src="${iconSrc}" style="width:10px;height:10px;vertical-align:middle;" /> ${param.seriesName}:</span>
                                <span style="font-weight:bold;margin-left:15px;">${param.value[1]} ${tooltipUnit ?? ""}</span>
                            </div>
                        `;
                    });

                    return tooltipText;
                },
            },
            grid: {
                left: "8%",
                right: "5%",
                top: "15%",
                bottom: "10%",
                containLabel: true,
            },
            xAxis: {
                type: "time",
                axisLabel: {
                    fontFamily: theme.typography.fontFamily,
                    color: theme.palette.primary.greyFourth,
                    fontSize: theme.typography.customFontSize[1],
                    formatter: (value) => {
                        const date = new Date(value);
                        const hours = date.getHours().toString().padStart(2, "0");
                        const minutes = date.getMinutes().toString().padStart(2, "0");
                        return `${hours}:${minutes}`;
                    },
                },
                axisLine: { lineStyle: { color: theme.palette.primary.greyThread } },
                axisTick: { show: false },
                splitLine: {
                    show: true,
                },
            },
            yAxis: {
                type: "value",
                name: yAxisName,
                nameLocation: "end",
                nameTextStyle: {
                    fontFamily: theme.typography.fontFamily,
                    color: theme.palette.primary.black,
                    fontSize: theme.typography.customFontSize[0],
                    lineHight: 1.33,
                    padding: [0, -15, 10, 0],
                },
                axisLabel: {
                    fontFamily: theme.typography.fontFamily,
                    color: theme.palette.primary.greyFourth,
                    fontSize: theme.typography.customFontSize[1],
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: theme.palette.primary.greyThread,
                        type: "dashed",
                    },
                },
            },
            series: lineChartData.map((serieItem) => ({
                name: serieItem.name,
                type: "line",
                showSymbol: false,
                data: serieItem.data,
                symbolSize: 8,
                lineStyle: styleSheet[serieItem.type].lineStyle,
                itemStyle: styleSheet[serieItem.type].itemStyle,
                areaStyle: styleSheet[serieItem.type].areaStyle,
            })),
        };

        chartInstance.current.setOption(option);

        // resize
        const resizeObserver = new ResizeObserver(() => {
            chart.resize();
        });
        resizeObserver.observe(chartDom);

        // clear
        return () => {
            resizeObserver.disconnect();
            chart.dispose();
        };
    }, [lineChartData, timeRangeUnit, timeRangePosition]);

    return (
        <div
            ref={chartRef}
            style={{
                width: "100%",
                height,
                background: theme.palette.primary.white,
            }}
        />
    );
}

export default LineChart;
